import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Layers } from 'lucide-react';
import { memo, useMemo } from 'react';
import { useVisibleMessages } from '../../hooks/use-visible-messages';
import { GO_DEEPER_MESSAGE, shouldOfferGoDeeper } from '../../lib/chat/depth';
import { useToolEvents } from '../../lib/tool-stream';
import { isApprovalResume } from './approvalResume';
import { ChatFeedbackProvider, ChatReadOnlyProvider } from './ChatFeedbackContext';
import { ChatLinksProvider, collectChatLinks } from './ChatLinksContext';
import { ChatMessageView } from './ChatMessage';
import { AskAnswersProvider, answeredAsks } from './shanks-tools/AskUserCard';
import { isMessagePartTool } from './types';
import { WhimsicalLoadingIndicator } from './WhimsicalLoadingIndicator';
const messageText = (m)=>m?.parts.map((p)=>p.type === 'text' ? p.text : '').join('\n') ?? '';
/** A reply that ends on a question or approval card waits for the user, not for "Go deeper". */ const awaitsDecision = (m)=>m.parts.some((p)=>{
        const out = 'output' in p ? p.output : undefined;
        return Boolean(out && typeof out === 'object' && (out._requiresApproval || out._askUser));
    });
/**
 * Memoized chat message component to prevent unnecessary re-renders
 */ const MemoChatMessage = /*#__PURE__*/ memo(ChatMessageView, (prev, next)=>prev.message === next.message && prev.chatId === next.chatId && prev.variant === next.variant);
/**
 * Renders a thread of chat messages with optimizations for streaming
 * @param props Component props
 * @returns Chat thread component
 */ export function ChatThread({ messages, isSending, chatId, variant = 'default', onFeedback, readOnly = false, onOpenAppLink, onSendPrompt }) {
    // Listen for optimistic save-log messages
    const { events: optimistic } = useToolEvents((ev)=>ev.type === 'data-chat-log-save-optimistic');
    // Filter messages to only show visible ones
    const visibleMessages = useVisibleMessages(messages);
    // Add optimistic messages to the end of the visible messages
    const withOptimistic = useMemo(()=>{
        if (optimistic.length === 0) return visibleMessages;
        const last = optimistic[optimistic.length - 1];
        if (last?.message) {
            return [
                ...visibleMessages,
                last.message
            ];
        }
        return visibleMessages;
    }, [
        visibleMessages,
        optimistic
    ]);
    const lastMessage = withOptimistic[withOptimistic.length - 1];
    const toolsStarted = lastMessage?.role === 'assistant' && lastMessage.parts.some(isMessagePartTool);
    const previousUser = [
        ...withOptimistic.slice(0, -1)
    ].reverse().find((m)=>m.role === 'user');
    const offerGoDeeper = Boolean(onSendPrompt) && !readOnly && !isSending && lastMessage?.role === 'assistant' && !awaitsDecision(lastMessage) && // After a card decision the reply is a confirmation, not an answer.
    !isApprovalResume(messageText(previousUser)) && shouldOfferGoDeeper(messageText(previousUser), messageText(lastMessage));
    const requestText = [
        ...withOptimistic
    ].reverse().find((message)=>message.role === 'user')?.parts.filter((part)=>part.type === 'text').map((part)=>part.type === 'text' ? part.text : '').join(' ') ?? '';
    const requestStatus = /pric|budget|cost|fund|excel|workbook/i.test(requestText) ? 'Preparing your pricing and funding response' : /compliance|requirement|rfp|solicitation/i.test(requestText) ? 'Preparing your requirements and compliance response' : /slide|brief|leadership/i.test(requestText) ? 'Preparing your leadership briefing' : /contract|deliver|staff|transition/i.test(requestText) ? 'Preparing your contract delivery response' : 'Working through your request';
    // Questions already answered in this thread, so their cards stay answered.
    const answerTexts = withOptimistic.filter((m)=>m.role === 'user').flatMap((m)=>m.parts.map((p)=>p.type === 'text' ? p.text : '')).filter((t)=>t.startsWith('[answers]'));
    const answersKey = answerTexts.join('\u0000');
    // biome-ignore lint/correctness/useExhaustiveDependencies: answersKey is the content of answerTexts
    const answers = useMemo(()=>answeredAsks(answerTexts), [
        answersKey
    ]);
    // The app links this thread may render: hrefs its tools returned. Kept
    // stable across streamed tokens so links re-render only when one arrives.
    const collected = collectChatLinks(withOptimistic, readOnly);
    const linkKey = [
        ...collected.hrefs
    ].sort().join('\n');
    // biome-ignore lint/correctness/useExhaustiveDependencies: linkKey is the content of collected
    const links = useMemo(()=>({
            ...collected,
            openInApp: onOpenAppLink
        }), [
        linkKey,
        onOpenAppLink
    ]);
    // Message objects retain identity during streaming, so memoized rows skip
    // unchanged content while saved decisions can still update the same row.
    const historyToRender = useMemo(()=>withOptimistic.slice(0, -1), [
        withOptimistic
    ]);
    return /*#__PURE__*/ _jsx(ChatReadOnlyProvider, {
        value: readOnly,
        children: /*#__PURE__*/ _jsx(AskAnswersProvider, {
            value: answers,
            children: /*#__PURE__*/ _jsx(ChatLinksProvider, {
                value: links,
                children: /*#__PURE__*/ _jsx(ChatFeedbackProvider, {
                    value: readOnly ? null : onFeedback ?? null,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: `${variant === 'assist' ? 'space-y-4' : variant === 'specra' ? 'space-y-[22px]' : 'space-y-8'}`,
                        children: [
                            historyToRender.map((m)=>/*#__PURE__*/ _jsx(MemoChatMessage, {
                                    message: m,
                                    chatId: chatId ?? '',
                                    variant: variant
                                }, m.id)),
                            lastMessage && /*#__PURE__*/ _jsx(ChatMessageView, {
                                message: lastMessage,
                                chatId: chatId ?? '',
                                variant: variant,
                                isActive: Boolean(isSending && lastMessage.role === 'assistant')
                            }, lastMessage.id),
                            offerGoDeeper && onSendPrompt && /*#__PURE__*/ _jsx("div", {
                                className: variant === 'specra' ? '-mt-3' : '-mt-6 pl-12',
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "text-muted-foreground",
                                    "data-testid": Testids.assistant.goDeeper,
                                    iconLeft: /*#__PURE__*/ _jsx(Layers, {}),
                                    onClick: ()=>onSendPrompt(GO_DEEPER_MESSAGE),
                                    children: "Go deeper"
                                })
                            }),
                            isSending && !toolsStarted && /*#__PURE__*/ _jsx(WhimsicalLoadingIndicator, {
                                variant: variant,
                                customMessages: [
                                    requestStatus
                                ]
                            })
                        ]
                    })
                })
            })
        })
    });
}
